import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { catchError, switchMap, take } from 'rxjs/operators';
import { markErrorEndedSession, markErrorToasted } from './toasted-errors';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { selectAuth, State } from '../../store/auth/auth.reducers';
import { selectEnrollmentRequired } from '../../store/twoFactor/two-factor.reducers';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AuthRefreshService, IdentitySwitchedError } from 'src/app/auth/services/auth-refresh.service';
import { AccessTokenHolder } from 'src/app/auth/services/access-token-holder.service';
import { captureReturnUrl } from 'src/app/auth/utils/return-url';
import { Auth } from 'src/app/auth/models/auth';
import { DialogService } from '../services/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { AppLocaleService } from '../services/app-locale.service';
import { logFilterDiag } from '../utils/filter-leak-diag';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import {
  CALLER_SHOWS_ERRORS,
  OFFLINE_DOWNLOAD,
  OFFLINE_FALLBACK,
  offlineModeError,
} from 'src/app/core/offline/offline-http';

// DIAGNOSTIC (filter-leak): recursively collect every `field` referenced in a
// dynacrud filter tree so we can spot foreign fields on an endpoint.
function collectFilterFields(filterBy: any, acc: string[] = []): string[] {
  if (!filterBy) {
    return acc;
  }
  if (typeof filterBy.field === 'string') {
    acc.push(filterBy.field);
  }
  if (Array.isArray(filterBy.filters)) {
    filterBy.filters.forEach((f: any) => collectFilterFields(f, acc));
  }
  return acc;
}

@Injectable()
export class SharedHttpInterceptor implements HttpInterceptor {
  private whitelistedUnauthorizedRoutes = /^.*(login|register).*$/;
  // Endpoints that must never trigger a refresh-retry (avoids loops). Includes
  // /v2/logout-all explicitly via the optional `-all` suffix -- relying on the
  // substring "logout" matching "/v2/logout-all" would let any future endpoint
  // whose name starts with "logout" (e.g. /v2/logout-status) slip in by accident.
  // The trailing \b prevents the regex from over-matching `/v2/loginx`-style
  // siblings either.
  private authEndpoints = /\/v2\/(login|refresh|logout(?:-all)?)\b/;
  // Session-machinery endpoints whose HTTP errors must NOT raise the generic interceptor
  // UI (the scary httpStatusWithMessage snack or the 403 navigate-home): refresh is
  // self-handled (a failure tears the session down via handle401's catch), and
  // logout/logout-all are best-effort with the local session already cleared and the user
  // navigated to /auth -- a 5xx snack there is noise about an action they perceive as done.
  // /v2/login is deliberately EXCLUDED: LoginComponent swallows its own error and relies on
  // the generic snack here for login-failure feedback.
  private silentErrorEndpoints = /\/v2\/(refresh|logout(?:-all)?)\b/;
  // 2FA wizard/settings endpoints: the bare status probe (GET /v2/profile/2fa) plus the step-up
  // submissions under it (enroll, verify, disable, regenerate backup codes, send-code). This regex is
  // used ONLY to keep the generic error snack silent for these endpoints (see intercept()): the calling
  // wizard/dialog renders its own inline error, and a generic toast on top would be double, contradictory
  // feedback. It does NOT influence the 401 refresh/teardown decision — a wrong code/password is a 400
  // (never reaches handle401), so a 401 here is always a genuine auth failure and takes the same
  // refresh-then-teardown path as any other protected call (see the NOTE in handle401). The
  // `(?:\/|$|\?)` boundary matches the bare `/v2/profile/2fa` — trailing slash, end-of-url, or query
  // string — as well as the sub-paths; a plain `/2fa/` would have missed the status probe.
  private stepUp2faEndpoints = /\/v2\/profile\/2fa(?:\/|$|\?)/;
  // Login-time resend of a MAIL/SMS 2FA challenge (POST /v2/login/2FA/resend). A 401 here means the
  // challenge token is invalid/expired -- LoginComponent.resendCode() owns that message and bounces back
  // to credentials itself (mirrors the /v2/login/2FA verify carve-out in handle401 below), so the generic
  // invalidCredentials snack there must stay silent for resend too, or the user would see BOTH the
  // generic "check your credentials" toast and resendCode()'s own "session expired" snack. Kept as its
  // OWN regex (not folded into the /v2/login/2FA\b test) so this carve-out is INTENTIONAL rather than
  // riding on that regex's substring match happening to also cover /resend -- a future narrowing of that
  // regex (e.g. anchoring it to end-of-path) could otherwise silently stop covering this endpoint too.
  private resendChallengeEndpoint = /\/v2\/login\/2FA\/resend\b/;
  // Avatar upload/removal (PUT/DELETE /v2/profile/avatar) plus the read probe. A rejected image is a
  // 400 carrying WHY it was rejected (wrong type, too large, unreadable), and the settings Profile
  // tab renders that inline next to the picture. The generic httpStatusWithMessage snack on top
  // would be a second, vaguer copy of the same message -- the same reasoning as the 2FA carve-out
  // above. It does NOT affect the 401 refresh/teardown decision: a 401 here is a genuine auth
  // failure and takes the normal path.
  private profileAvatarEndpoint = /\/v2\/profile\/avatar(?:\/|$|\?)/;
  // Kiosk fleet administration (/v2/kiosk-devices/**): the device detail, the provisioning wizard's
  // enrollment POSTs and the two administration PATCHes. Every one of those surfaces renders its own
  // message, chosen from the contract's numeric errorCode, and each code carries a specific next
  // move: 8212 (illegal transition) is a 409 whose answer is "use Re-enroll", 8215 (CA not
  // configured on this environment) is a 500 whose answer is "tell ops, do not retry", 8213 reloads
  // the shop list. The generic snack raises a bare "HTTP «status»" for anything that is not a 403 or
  // a 404, so without this carve-out the operator gets the useful explanation AND the raw code --
  // and it is the second one that ends up in the support report. The three 404s already pass
  // silently; they are inside this regex by INTENTION rather than by coincidence, so a future change
  // to the 404 branch cannot quietly start double-messaging them. Same shape and same reasoning as
  // the 2FA and avatar carve-outs above; like them it does NOT affect the 401 refresh/teardown
  // decision, where a kiosk call is an ordinary protected request.
  //
  // The membership test is `ownsKioskMessage(request)`, NOT this regex on its own -- see there for
  // the one kiosk call that owns no message and must keep the generic snack.
  private kioskAdminEndpoints = /\/v2\/kiosk-devices(?:\/|$|\?)/;
  // The kiosk COLLECTION ROOT, i.e. `/v2/kiosk-devices` with nothing after it (the fleet grid passes
  // its criteria as an `HttpParams` `searchRequest`, which `HttpRequest.url` does not carry, so the
  // `$` branch is the one that matches it; the `\?` branch covers a caller that inlines the query).
  private kioskFleetRoot = /\/v2\/kiosk-devices(?:$|\?)/;
  // Active-session management (GET /v2/me/sessions, POST /v2/me/sessions/logout). BOTH consumers own
  // their message: the settings' Security tab snacks `settings.security.sessions.loadError` /
  // `logoutSelectedError`, and the kiosk wizard's closing step raises its own banner and tells the
  // operator to sign out from the user menu instead. Without the carve-out each of them stacked a
  // bare "HTTP «status»" on top of its own sentence -- and on the kiosk step, which is the one that
  // decides whether a privileged session is left alive on a shop counter, the raw code is what ends
  // up in the support report. Same shape and same reasoning as the four carve-outs above; like them
  // it does NOT affect the 401 refresh/teardown decision, where these are ordinary protected calls.
  // NOTE the `/me/` segment: /v2/sessions is the warehouse session CRUD and is untouched by this.
  private meSessionsEndpoints = /\/v2\/me\/sessions(?:\/|$|\?)/;
  // Order file import preview (POST /v2/orders/import/preview). A refused file is a 400 whose body
  // says WHY in the user's language, and the import dialog renders that inline; the generic snack
  // would show Angular's "Http failure response for <url>" instead, which names nothing the user
  // can fix. Same shape and reasoning as the avatar carve-out; it does NOT affect the 401
  // refresh/teardown decision.
  private orderImportPreviewEndpoint = /\/v2\/orders\/import\/preview(?:$|\?)/;
  // The storage-failure probe's report (POST /v2/userDrafts/diag:*, see QuotaDiagReporterService).
  // It is fire-and-forget telemetry with no user waiting on it, and it fires precisely when the
  // session may already be in trouble -- so it must take NONE of the reactions below. A 401 here
  // would otherwise enter handle401 and tear the session down, i.e. a diagnostic would log the user
  // out; any other status would raise the generic snack for a request the user never made. Matched
  // on the `diag:` key prefix rather than the whole endpoint so genuine draft calls keep their
  // normal 401 handling.
  private diagReportEndpoint = /\/v2\/userDrafts\/diag:/;
  // The push-subscription endpoint, carved out for EVERY method -- nothing on it is fired by a
  // user gesture or blocks one.
  //   - DELETE fires from clearSession(), i.e. exactly when a 401 is most likely. Left in the
  //     normal path it would trigger refresh-then-teardown and re-enter the teardown it was
  //     called from.
  //   - POST is no longer a user gesture either. Since the heartbeat (PushNotificationService)
  //     it fires on every access-token rotation, so it is background traffic the user never
  //     asked for: a failure there must not raise the generic snack, and a 401 whose refresh
  //     fails must not run endSession() -- a background call with session-ending power is the
  //     exact hazard diagReportEndpoint's carve-out exists to state. Worse, the normal path is
  //     self-refeeding: POST -> 401 -> refresh() -> applyToken -> tokenHolder.set -> a NEW
  //     heartbeat -> POST -> 401 -> ..., unbounded for as long as refresh keeps succeeding.
  //   - PATCH (the personal/shared toggle) is a gesture, but not a blocking one: setShared()
  //     already degrades silently and the dialog restores the control from the returned flag.
  // Losing refresh-then-retry costs nothing here: subscribeAndPost() degrades to `inactive`, and
  // the next rotation or authenticated transition re-POSTs anyway.
  //
  // KNOWN COST, ON THE PATCH ALONE, AND IT IS NOT "nothing" the way the line above says of the
  // other two. The POST and DELETE are re-fired by something else -- the heartbeat on the next
  // rotation, the teardown that is running anyway -- but the toggle is a LONE request with no
  // companion call, so with an expired bearer it 401s, gets no refresh, and setShared() returns
  // false and snaps the control back with no message. The next press does exactly the same thing:
  // nothing in the dialog can rotate the token, so the toggle stays dead until some unrelated call
  // elsewhere in the app refreshes. (The POST paths self-heal because trySubscribeAndPost() hits
  // /v2/push/vapid-key first and that endpoint is NOT carved out, so it takes the refresh.) Left
  // as it is deliberately: giving the PATCH the retry means splitting this carve-out by method,
  // and a method-split carve-out is one edit away from handing the POST its self-refeeding loop
  // back. A dead toggle recovers on the next navigation; that loop does not recover at all.
  //
  // ANCHORED, like every sibling carve-out above -- and the anchor is now load-bearing rather than
  // defensive: `/v2/push/devices/permission` is a REAL sibling that must NOT inherit this
  // exemption, and a bare `includes()` would hand it no-refresh + no-snack + no-teardown. There is
  // no legitimate suffix to allow: this endpoint takes its device argument in the BODY, never in
  // the path, so end-of-url or a query string is the whole set. The spec pins this literal against
  // PUSH_DEVICES_ENDPOINT so it cannot drift from the URL PushApiService calls.
  private pushDevicesEndpoint = /\/v2\/push\/devices(?:$|\?)/;
  // The push endpoints the app calls BY ITSELF: the capability probe on every authenticated
  // transition, and the permission report whenever the browser's permission state changes. Neither is
  // a user action, so neither may raise the generic httpStatusWithMessage snack -- with the backend
  // down a network failure is status 0, which is not 401/403/404 and would otherwise toast on every
  // login for a request the user never made and cannot act on.
  //
  // Unlike pushDevicesEndpoint these are NOT exempt from 401 refresh-retry: they carry no
  // self-feeding loop (the heartbeat POST is the one that could re-trigger itself through refresh),
  // and a genuine 401 should refresh and retry exactly like any other authenticated call. They ARE
  // exempt from the TEARDOWN a failed refresh would otherwise run — see pushNoTeardownEndpoints.
  //
  // The admin device read (/v2/push/users/{id}/devices) is here too, and an earlier version of this
  // comment left it out on the grounds that "that one IS a user action -- somebody opened a
  // screen". That was wrong about the trigger: ViewUserComponent calls loadPushDevices() from its
  // `route.params` subscription, so it fires on opening ANY user for edit, not on reaching for the
  // push panel. The reaction it was inheriting is the generic branch's 403 pair — a snack AND
  // router.navigateByUrl(homePage) — so an account that may edit users but is not scoped to the
  // push admin read was thrown out of the form, losing whatever it had typed, every single time it
  // opened one; and the component's own pushDevices.loadFailed copy, written for exactly this, could
  // never appear because the navigation happened first. Silencing the generic branch is what lets
  // that inline message do its job. This is the "a call the user never made must not raise a snack"
  // rule applied to the one push endpoint whose trigger was misread.
  private pushBackgroundEndpoints = /\/v2\/push\/(vapid-key|devices\/permission|users\/\d+\/devices)\b/;

  // The push endpoints that must never END A SESSION. A failed refresh normally tears down, which
  // is right for a call the user is waiting on and wrong for one they never made: reportPermission()
  // is fire-and-forget background traffic (the permission watcher, and every status read), so with
  // an expired refresh cookie, flipping notifications off in the browser's site settings could log
  // the user out of the app and discard unsaved work. That is precisely the hazard stated for
  // diagReportEndpoint one field up — "telemetry must never be able to end a session" — and it
  // reached this endpoint because the carve-out above was reasoned about the SNACK and the retry
  // loop, not about the teardown. The retry itself is kept (see pushBackgroundEndpoints): refreshing
  // and replaying a permission report is harmless and self-healing. Only endSession() is withheld,
  // so the session dies on the user's next real call instead of on a watcher's.
  private pushNoTeardownEndpoints = /\/v2\/push\/(vapid-key|devices\/permission)\b/;
  // The one API call an OFFLINE-MODE tab still sends: the reconnect probe (OfflineSessionService ->
  // AuthRefreshService.refresh). Anchored like the push carve-outs above.
  private offlineReconnectEndpoint = /\/v2\/refresh(?:$|\?)/;
  private auth!: Auth;
  // Whether the current session is a forced-2FA-enrollment session (route-scoped to /v2/profile/2fa/**).
  // Read in handle401 to keep a stray non-2FA 401 from ejecting the user out of the wizard (see there).
  private enrollmentRequired = false;

  constructor(
    private store: Store<State>,
    private router: Router,
    private dialog: DialogService,
    private authService: AuthService,
    private authRefresh: AuthRefreshService,
    private translateService: TranslateService,
    private tokenHolder: AccessTokenHolder,
    private localeService: AppLocaleService,
    private offlineMode: OfflineModeState
  ) {
    this.store.pipe(select(selectAuth)).subscribe(auth => this.auth = auth);
    this.store.pipe(select(selectEnrollmentRequired)).subscribe(required => this.enrollmentRequired = required);
  }

  // DIAGNOSTIC (filter-leak): capture leaking filters at the HTTP boundary.
  // Every request passes through here regardless of which component/code path
  // built it, so this catches leaks the component-level hooks miss. Logs only
  // suspicious requests to the two endpoints known to reject foreign filters:
  //   - /v2/sessions/{id}/movements  → any structured filterBy is foreign
  //     (the legit movements list only ever sends { text } / pagination).
  //   - /v2/stocks                   → `order.*` and session-list fields are
  //     foreign (the `order` entity does not exist on the stock entity).
  private captureLeakDiag(request: HttpRequest<any>): void {
    try {
      if (request.method !== 'GET') {
        return;
      }
      const url = request.url || '';
      const movementsMatch = url.match(/\/v2\/sessions\/(\d+)\/movements$/);
      const isStocks = /\/v2\/stocks$/.test(url);
      if (!movementsMatch && !isStocks) {
        return;
      }

      const raw = request.params?.get('searchRequest');
      if (!raw) {
        return;
      }

      let decoded: any;
      try {
        decoded = JSON.parse(atob(raw));
      } catch {
        return;
      }

      const filter = decoded?.filter;
      const fields = collectFilterFields(filter?.filterBy);

      if (movementsMatch) {
        // The movements list legitimately sends no filterBy — any is a leak.
        if (filter?.filterBy) {
          logFilterDiag('http.movements.leak', {
            url,
            pathSessionId: movementsMatch[1],
            fields,
            filter,
            pagination: decoded?.pagination,
          });
        }
      } else if (isStocks) {
        const FOREIGN = ['order.', 'sessionId', 'systemSession', 'stateId'];
        const foreignFields = fields.filter(f => FOREIGN.some(p => f === p || f.startsWith(p)));
        if (foreignFields.length > 0) {
          logFilterDiag('http.stocks.leak', {
            url,
            foreignFields,
            fields,
            filter,
            pagination: decoded?.pagination,
          });
        }

        // A leaked SORT breaks /v2/stocks the same way a leaked filter does
        // (`UnknownPathException: Could not resolve attribute 'priority'`), and the
        // filter check above cannot see it — the filter that rides along is the cart's
        // own and looks perfectly healthy.
        //
        // "Foreign" here means foreign TO `Stock`, and nothing else. Every name below is a
        // perfectly ordinary sortable column somewhere else in the app — `priority` and
        // `deadline` on the dashboard orders list, `expiration` on completed orders — so this
        // list is only meaningful inside the `isStocks` branch it sits in. Do NOT hoist it to
        // module scope or reuse it for another endpoint: it would report healthy sorts as leaks
        // and bury the real ones. A different endpoint needs its own list, derived from ITS entity.
        const FOREIGN_SORT = ['priority', 'orderStatusId', 'paymentStatus', 'deadline', 'expiration', 'motivation'];
        const sortFields = (Array.isArray(decoded?.sort) ? decoded.sort : [])
          .map((s: { field?: string }) => s?.field)
          .filter((f: string | undefined): f is string => !!f);
        const foreignSort = sortFields.filter((f: string) => FOREIGN_SORT.some(p => f === p || f.startsWith(`${p}.`)));
        if (foreignSort.length > 0) {
          logFilterDiag('http.stocks.sortLeak', {
            url,
            foreignSort,
            sortFields,
            src: filter?._src,
            route: decoded?._route,
            pagination: decoded?.pagination,
          });
        }
      }
    } catch {
      // Diagnostics must never break a request.
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Diagnostic capture runs FIRST so the leak detector sees every outgoing request
    // (the inspected URL patterns are all under apiUrl today, but keeping it pre-gate
    // means a future non-API leak would still surface).
    this.captureLeakDiag(request);
    // Same-origin / apiUrl allow-list. Without this, a future HttpClient call to a
    // third-party host (analytics, maps, a partner API) would ship the user's bearer
    // token + refresh cookie cross-origin. There is no such call in the tree today,
    // but the interceptor is opt-out by default for everything HttpClient touches --
    // make the security boundary explicit at the perimeter.
    if (!request.url.startsWith(environment.apiUrl)) {
      return next.handle(request);
    }
    // OFFLINE MODE (OfflineModeState): the tab booted with no network on a remembered identity and
    // holds no session and no bearer. Every API call but the reconnect probe is refused HERE, without
    // touching the network, as a status-0 error: whatever root service or shell component fires one
    // gets the "did not get through" it would get from a dead network, and none of it can reach
    // handle401 -- so no refresh-then-teardown can ever start from an offline tab -- nor raise the
    // generic snack (returned before the catchError below). The catalogue reads IndexedDB instead.
    if (this.offlineMode.active && !this.offlineReconnectEndpoint.test(request.url)) {
      return throwError(() => offlineModeError(request.url));
    }
    request = this.addContext(request);

    return next.handle(request).pipe(
      catchError((error) => {
        // The diagnostic report owns nothing and is owed nothing: no refresh-retry, no teardown,
        // no snack. Checked before every other branch so no later change can accidentally give a
        // telemetry request the power to end a user's session.
        if (this.diagReportEndpoint.test(request.url)) {
          return throwError(error);
        }
        // The push-subscription endpoint, EVERY method. Same reasoning as diagReportEndpoint: a
        // call the user never made must never be able to end a session, and must never raise a
        // snack about a failure they cannot act on. The DELETE fires from teardown itself; the
        // POST fires from the heartbeat on every token rotation (and would otherwise re-feed
        // itself through refresh). See the field declaration for the full reasoning.
        if (this.pushDevicesEndpoint.test(request.url)) {
          return throwError(error);
        }
        if (error instanceof HttpErrorResponse && !(error.error instanceof ErrorEvent)) {
          if (error.status === 401) {
            return this.handle401(request, next, error);
          }
          this.reactToError(request, error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * The generic reaction to a NON-401 HTTP error: the forbidden snack (and home), or the
   * httpStatusWithMessage snack, for every endpoint that does not own its own message. Shared by the
   * first attempt and by the retry after a refresh, which used to fall into the refresh's
   * catchError and be read as a failed REFRESH -- a 500 on the retry ended the session (MR !197
   * review, round 15).
   */
  private reactToError(request: HttpRequest<any>, error: HttpErrorResponse): void {
    // Session-machinery endpoints own their outcomes -- skip the generic UI reactions
    // (forbidden snack + navigate-home, or the httpStatusWithMessage snack) so a
    // logout/refresh failure doesn't toast the user mid-teardown / after they're
    // already logged out. login is NOT silent (see silentErrorEndpoints).
    // The step-up 2FA endpoints are ALSO skipped here: their 401s are already handled inline in
    // handle401, but a NON-401 (a 5xx/network error on enroll/verify/disable/regenerate/status)
    // would otherwise fire the generic httpStatusWithMessage snack ON TOP of the calling
    // dialog/wizard's own inline error — double, contradictory feedback ("wrong password" inline
    // + a generic error toast). The dialog owns the feedback; stay silent here.
    // The login challenge-resend endpoint is skipped for the SAME reason on NON-401s (esp. a 429
    // rate limit): LoginComponent.resendCode() surfaces its own "wait" / generic message, so the
    // generic httpStatusWithMessage snack here would stack a raw "HTTP 429" on top of it. (The 401
    // carve-out for it lives in handle401; this closes the non-401 half.)
    if (!this.silentErrorEndpoints.test(request.url) && !this.stepUp2faEndpoints.test(request.url)
        && !this.resendChallengeEndpoint.test(request.url)
        && !this.profileAvatarEndpoint.test(request.url)
        && !this.ownsKioskMessage(request)
        && !this.meSessionsEndpoints.test(request.url)
        && !this.orderImportPreviewEndpoint.test(request.url)
        && !this.pushBackgroundEndpoints.test(request.url)
        // A request that falls back to the offline catalogue on a network failure: its caller
        // answers from IndexedDB, so a status-0 "HTTP 0" toast would be noise (offline-http.ts).
        && !(error.status === 0 && request.context.get(OFFLINE_FALLBACK))
        // A background download of offline data (Scan's Modalita' offline): nobody waits on it,
        // the offline screen shows the outcome per game and retries -- no snack for any status,
        // and no 403 bounce home from under the Scan screens (offline-http.ts).
        && !request.context.get(OFFLINE_DOWNLOAD)
        // A read whose caller says its failure inline, with "Riprova" (Scan's strict card data):
        // a snack on top would say it twice, once per card (offline-http.ts).
        && !request.context.get(CALLER_SHOWS_ERRORS)) {
      if (error.status === 403) {
        this.dialog.openSnack(this.translateService.instant('common.error.forbidden'), 'info');
        markErrorToasted(error);
        this.router.navigateByUrl(environment.homePage).then();
      } else if (error.status !== 404) {
        this.dialog.openSnack(this.translateService.instant('common.error.httpStatusWithMessage', { status: error.status, message: error.message }), 'info');
        markErrorToasted(error);
      }
    }
  }

  /**
   * True for a `/v2/kiosk-devices` call whose CALLER renders the failure itself, which is what the
   * carve-out above is for. It is not the whole prefix, and the difference is one route.
   *
   * Everything under `/v2/kiosk-devices/**` owns its message -- the device detail's banner, the
   * wizard's inline error -- and so does the `POST` to the collection root, which is
   * `KioskApiService.createDevice()`, i.e. the wizard's assign step. The one that owns NOTHING is a
   * `GET` on the collection root: that URL is the fleet GRID's search, and the grid is a plain
   * `DefaultListComponent`, whose error arm dispatches `Loading false` and rethrows without
   * rendering anything at all. Silencing it left a 500 on the fleet search showing an empty table
   * and no message whatsoever -- strictly worse than the bare "HTTP «500»" it replaced, because an
   * empty table is indistinguishable from a fleet with no devices in it.
   *
   * The wizard's own label-proposal read (`KioskApiService.listDevices()`) shares that exact URL and
   * is therefore no longer silenced either. That is deliberate rather than collateral: its error arm
   * only sets `fleetLoaded = false`, which hides the "this shop already has N kiosks" line and says
   * nothing, so the generic snack is the only account the operator gets of why the count vanished.
   * The label is typed by hand either way, so the wizard still works.
   */
  private ownsKioskMessage(request: HttpRequest<any>): boolean {
    if (!this.kioskAdminEndpoints.test(request.url)) {
      return false;
    }
    return !(request.method === 'GET' && this.kioskFleetRoot.test(request.url));
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    // A 401 only warrants a session teardown if the user actually HAD a session.
    // On a cold-load APP_INITIALIZER bootstrap probe to /v2/refresh (or a failed
    // login), the user is unauthenticated and the 401 is expected — tearing down
    // here would spuriously navigate and broadcast a cross-tab logout for a session
    // that never existed (and warn about navigation outside the Angular zone).
    const hadSession = !!this.auth?.isAuthenticated;

    // Forced-enrollment backstop. An enrollment session is authenticated but route-scoped to
    // /v2/profile/2fa/** only, so a 401 from ANY OTHER protected endpoint means a stray consumer fired
    // while the user is in the wizard (a background/root service the per-consumer enrollment gates
    // missed). Taking the normal path below would refresh → re-mint another enrollment token → retry →
    // 401 again → endSession(), ejecting the user from /auth/enroll to /auth/login mid-enrollment. So
    // do NOT tear down here: let the stray call error where it stands and leave the user in the wizard.
    // This is the CENTRAL backstop behind the individual consumer gates (date-format / release-notes /
    // stats-stream). The 2FA endpoints and the auth endpoints are deliberately NOT gated, so a genuinely
    // revoked enrollment session still tears down via its own /v2/profile/2fa or /v2/refresh 401.
    if (this.enrollmentRequired && hadSession
        && !this.authEndpoints.test(request.url) && !this.stepUp2faEndpoints.test(request.url)) {
      return throwError(error);
    }

    // NOTE on step-up 2FA endpoints (/v2/profile/2fa/*): a WRONG submitted code/password is NOT a 401 —
    // the backend returns 400 (BusinessException TFA_INVALID_CODE/TFA_INVALID_PASSWORD), which never
    // reaches this handler (it stays a non-401 the calling dialog renders inline; intercept() keeps the
    // generic snack silent for these endpoints). So a 401 on a step-up endpoint is ONLY a genuine
    // auth failure — a revoked/invalidated session (logout-all bumps tokens_valid_from, or the sid left
    // the active families) or an expired/absent bearer — and MUST take the normal refresh-then-teardown
    // path below, exactly like any other protected call. It must NOT be treated as a recoverable
    // "wrong code" kept inline: a revoked session held inline would trap the user retyping a correct
    // credential forever, never bouncing them to re-login. (Historically this handler special-cased a
    // present-and-valid bearer as the wrong-code case and self-handled it — that rested on the false
    // premise that a wrong code is a 401.)

    // Do not refresh-retry for the auth endpoints themselves or whitelisted routes.
    // When the user had a session, clear it (so LoginComponent.ngOnInit's reactive
    // nav can't bounce them back to consumeReturnUrl() and re-fire the 401 in a loop).
    if (this.authEndpoints.test(request.url) || this.whitelistedUnauthorizedRoutes.test(request.url)) {
      if (hadSession) {
        this.endSession();
        markErrorEndedSession(error);
      }
      // A 401 on a user-initiated FIRST-factor login (or register) means bad credentials — surface it.
      // This is the ONLY place that feedback can fire: a 401 short-circuits intercept()'s catchError
      // into handle401 before the generic snack, and LoginComponent swallows the error itself.
      // refresh/logout 401s are session machinery and stay silent (silentErrorEndpoints), matching the
      // generic-snack gate in intercept(). Without this, first-factor bad-credential 401s show NOTHING.
      //
      // The SECOND factor (/v2/login/2FA) is deliberately EXCLUDED: a 401 there is a wrong code OR an
      // expired challenge token, and only LoginComponent.submitCode can tell them apart (it holds the
      // challenge token to decode). It owns that message so the wording matches the action — "session
      // expired, sign in again" when it bounces to credentials vs "invalid code, try again" when it
      // keeps the user on the code step. A single generic snack here could only ever be one of the two.
      if (!this.silentErrorEndpoints.test(request.url) && !/\/v2\/login\/2FA\b/.test(request.url) && !this.resendChallengeEndpoint.test(request.url)) {
        this.dialog.openSnack(this.translateService.instant('auth.login.invalidCredentials'), 'warning');
        markErrorToasted(error);
      }
      return throwError(error);
    }
    // The retry's own failure is caught INSIDE the switchMap, never by the refresh's catchError
    // below: that one is for a refresh that failed. With the retry's error falling through to it, a
    // request that 401'd, refreshed fine and then got a 500 on the retry was read as a dead refresh
    // cookie and ended the session -- the user logged out over a server error (MR !197 review,
    // round 15).
    return this.authRefresh.refresh().pipe(
      take(1),
      catchError((refreshError) => {
        // NOT a failed refresh: the tab was handed to a DIFFERENT user and AuthRefreshService has
        // already applied that session in full (bearer, store, broadcast) before aborting — it aborts
        // precisely so the retry above does not replay the OUTGOING user's request under the incoming
        // bearer. Tearing down here would sign the user who just arrived straight back out. Surface
        // the ORIGINAL 401 instead of the internal sentinel: from the caller's side the request simply
        // failed unauthorized, which is what its own error handling is written against.
        if (refreshError instanceof IdentitySwitchedError) {
          // The switch raises its own notice; the caller must not explain a failure of its own.
          markErrorEndedSession(error);
          return throwError(error);
        }
        // A background push call may refresh and retry, but it may not END the session when that
        // refresh fails — see pushNoTeardownEndpoints. The user made no request here, so a dead
        // refresh cookie has to be discovered by something they are actually waiting on.
        // Same for a background download of offline data (OFFLINE_DOWNLOAD, offline-http.ts).
        if (hadSession && !this.pushNoTeardownEndpoints.test(request.url)
            && !request.context.get(OFFLINE_DOWNLOAD)) {
          this.endSession();
          markErrorEndedSession(refreshError);
        }
        return throwError(refreshError);
      }),
      switchMap((token) => next.handle(this.withAuthHeader(request, token)).pipe(
        catchError((retryError) => {
          if (retryError instanceof HttpErrorResponse && !(retryError.error instanceof ErrorEvent)) {
            // A 401 on a request carrying a token minted a moment ago is a session the backend no
            // longer honours (revoked, logged out everywhere): the teardown the old path gave it.
            // No second refresh -- that is the loop this method exists to avoid.
            if (retryError.status === 401) {
              if (hadSession && !this.pushNoTeardownEndpoints.test(request.url)
                  && !request.context.get(OFFLINE_DOWNLOAD)) {
                this.endSession();
                // The login page is the explanation: a caller must not add "not added to the cart"
                // (MR !197 review, round 16).
                markErrorEndedSession(retryError);
              }
            } else {
              this.reactToError(request, retryError);
            }
          }
          return throwError(retryError);
        }),
      )),
    );
  }

  /** Capture the destination for post-relogin restore, then clear the session locally. */
  private endSession(): void {
    // endSession only runs when a session existed (hadSession), so this.auth is the expiring user.
    // Stamp the captured deep-link as theirs so a DIFFERENT user signing into this tab afterwards
    // (per-tab sessionStorage survives the logout) isn't restored onto the previous user's page.
    captureReturnUrl(this.router.url, this.auth?.userId);
    this.authService.clearSession();
  }

  private addContext(request: HttpRequest<any>): HttpRequest<any> {
    const headers: { [k: string]: string } = {
      'Content-Type': 'application/json',
      'api-key': environment.apiKey,
      'cache-control': environment.cacheControl,
      'context': 'User',
      // Forward the active UI locale (multilanguage feature) so the backend can
      // localize responses. Lives here in addContext, the single place this rewrite
      // builds request headers.
      'Accept-Language': this.localeService.current
    };
    // Bearer lives in AccessTokenHolder, not in the store, so the secret can't ride along with
    // state-tree persistence (metareducer -> localStorage) or devtools snapshots.
    const tok = this.tokenHolder.get();
    if (this.auth && this.auth.isAuthenticated && tok && tok.value) {
      headers['Authorization'] = `${tok.type} ${tok.value}`;
    }
    // A multipart upload must leave Content-Type unset: the browser writes it together with the
    // part boundary, and a forced application/json makes the backend reject the body.
    if (request.body instanceof FormData) {
      delete headers['Content-Type'];
    }
    return request.clone({ setHeaders: headers, withCredentials: true });
  }

  private withAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: true });
  }
}
